/* eslint-disable */

export const drawCompositePreview = (container, dotsPassedIn) => {
  /**
   * A set of random utilities.
   *
   * The randomizer is built in two parts:
   *
   * - Uses two murmur2 32-bit hashes with different seeds
   *   on the set of input bytes (i.e. the token hash) to get a 64-bit value
   * - Uses PCG to get a random 0..1 value, extracted from Jacob Rus' notebook
   *
   * See here for more:
   * https://gist.github.com/mattdesl/779daf4c9fa72e21733f9db928f993aa
   * https://github.com/mattdesl/canvas-sketch-util/blob/master/random.js
   */

  // Note that the index order [0, 1, 2, 3] is little-endian
  const eps = Math.pow(2, -32),
    m0 = 0x7f2d,
    m1 = 0x4c95,
    m2 = 0xf42d,
    m3 = 0x5851, // 6364136223846793005
    a0 = 0x814f,
    a1 = 0xf767,
    a2 = 0x7b7e,
    a3 = 0x1405; // 1442695040888963407

  const state = new Uint16Array(4);
  const dv = new DataView(state.buffer);

  let _nextGaussian = null;
  let _hasNextGaussian = false;

  // random value between 0..1
  const value = () => {
    // Advance internal state
    const s0 = state[0],
      s1 = state[1],
      s2 = state[2],
      s3 = state[3],
      new0 = (a0 + m0 * s0) | 0,
      new1 = (a1 + m0 * s1 + (m1 * s0 + (new0 >>> 16))) | 0,
      new2 = (a2 + m0 * s2 + m1 * s1 + (m2 * s0 + (new1 >>> 16))) | 0,
      new3 = a3 + m0 * s3 + (m1 * s2 + m2 * s1) + (m3 * s0 + (new2 >>> 16));
    (state[0] = new0), (state[1] = new1), (state[2] = new2);
    state[3] = new3;

    // Calculate output function (XSH RR), uses old state
    const xorshifted =
        (s3 << 21) + (((s3 >> 2) ^ s2) << 5) + (((s2 >> 2) ^ s1) >> 11),
      out_int32 =
        (xorshifted >>> (s3 >> 11)) | (xorshifted << (-(s3 >> 11) & 31));
    return eps * (out_int32 >>> 0);
  };

  // internally gets a 32-bit from tokenData hash bytes
  const hash32 = (bytes, seed = 0) => {
    // murmur2 32bit
    // https://github.com/garycourt/murmurhash-js/blob/master/murmurhash2_gc.js
    const K = 16;
    const mask = 65535;
    const maskByte = 0xff;
    var m = 0x5bd1e995;
    var l = bytes.length,
      h = seed ^ l,
      i = 0,
      k;
    while (l >= 4) {
      k =
        (bytes[i] & maskByte) |
        ((bytes[++i] & maskByte) << 8) |
        ((bytes[++i] & maskByte) << 16) |
        ((bytes[++i] & maskByte) << 24);
      k = (k & mask) * m + ((((k >>> K) * m) & mask) << K);
      k ^= k >>> 24;
      k = (k & mask) * m + ((((k >>> K) * m) & mask) << K);
      h = ((h & mask) * m + ((((h >>> K) * m) & mask) << K)) ^ k;
      l -= 4;
      ++i;
    }
    switch (l) {
      case 3:
        h ^= (bytes[i + 2] & maskByte) << K;
      case 2:
        h ^= (bytes[i + 1] & maskByte) << 8;
      case 1:
        h ^= bytes[i] & maskByte;
        h = (h & mask) * m + ((((h >>> K) * m) & mask) << K);
    }
    h ^= h >>> 13;
    h = (h & mask) * m + ((((h >>> K) * m) & mask) << K);
    h ^= h >>> 15;
    return h >>> 0;
  };

  // sets the seed to a tokenData hash string "0x..."
  const set_seed = (hash) => {
    _hasNextGaussian = false;
    _nextGaussian = null;
    const nBytes = ~~((hash.length - 2) / 2);
    const bytes = [];
    for (let j = 0; j < nBytes; j++) {
      const e0 = 2 + 2 * j;
      bytes.push(parseInt(hash.slice(e0, e0 + 2), 16));
    }

    // to keep it simple, we just use 32bit murmur2 with two different seeds
    const seed_a = 1690382925;
    const seed_b = 72970470;
    const lower = hash32(bytes, seed_a);
    const upper = hash32(bytes, seed_b);
    dv.setUint32(0, lower);
    dv.setUint32(4, upper);
  };

  // random boolean with 50% uniform chance
  const boolean = () => value() > 0.5;

  // random chance
  const chance = (n = 0.5) => value() < n;

  // random value between min (inclusive) and max (exclusive)
  const range = (min, max) => {
    if (max === undefined) {
      max = min;
      min = 0;
    }
    return value() * (max - min) + min;
  };

  // random value between min (inclusive) and max (exclusive), then floored
  const rangeFloor = (min, max) => Math.floor(range(min, max));

  // pick a random element in the given array
  const pick = (array) =>
    array.length ? array[rangeFloor(array.length)] : undefined;

  // shuffle an array
  const shuffle = (arr) => {
    var rand;
    var tmp;
    var len = arr.length;
    var ret = [...arr];
    while (len) {
      rand = ~~(value() * len--);
      tmp = ret[len];
      ret[len] = ret[rand];
      ret[rand] = tmp;
    }
    return ret;
  };

  // random point in a uniform 2D disc with given radius
  function insideCircle(radius = 1, out = []) {
    var theta = value() * 2.0 * Math.PI;
    var r = radius * Math.sqrt(value());
    out[0] = r * Math.cos(theta);
    out[1] = r * Math.sin(theta);
    return out;
  }

  // weighted randomness, specify weights array and the return value is an index
  const weighted = (weights) => {
    var totalWeight = 0;
    var i;

    for (i = 0; i < weights.length; i++) {
      totalWeight += weights[i];
    }

    var random = value() * totalWeight;
    for (i = 0; i < weights.length; i++) {
      if (random < weights[i]) {
        return i;
      }
      random -= weights[i];
    }
    return 0;
  };

  // random gaussian distribution
  const gaussian = (mean = 0, standardDerivation = 1) => {
    // https://github.com/openjdk-mirror/jdk7u-jdk/blob/f4d80957e89a19a29bb9f9807d2a28351ed7f7df/src/share/classes/java/util/Random.java#L496
    if (_hasNextGaussian) {
      _hasNextGaussian = false;
      var result = _nextGaussian;
      _nextGaussian = null;
      return mean + standardDerivation * result;
    } else {
      var v1 = 0;
      var v2 = 0;
      var s = 0;
      do {
        v1 = value() * 2 - 1; // between -1 and 1
        v2 = value() * 2 - 1; // between -1 and 1
        s = v1 * v1 + v2 * v2;
      } while (s >= 1 || s === 0);
      var multiplier = Math.sqrt((-2 * Math.log(s)) / s);
      _nextGaussian = v2 * multiplier;
      _hasNextGaussian = true;
      return mean + standardDerivation * (v1 * multiplier);
    }
  };

  const createHashFromHashes = (hashes) => {
    let resultBytes = [];
    let hashIndex = 0;
    let byteIndex = 0;

    while (resultBytes.length < 32) {
      if (hashIndex < hashes.length) {
        // Extract one byte from the current hash
        const hash = hashes[hashIndex].substring(2); // Remove '0x' prefix
        const byte = hash.slice(byteIndex * 2, byteIndex * 2 + 2);
        resultBytes.push(byte);

        // Move to the next hash, or the next byte in each hash if all hashes have been used
        hashIndex = (hashIndex + 1) % Math.min(hashes.length, 32);
        if (hashIndex === 0) {
          byteIndex++;
        }
      } else {
        // If there are less than 32 hashes, move to the next byte in each hash
        byteIndex++;
        hashIndex = 0;
      }
    }

    // Join the result bytes into a single string and prefix with '0x'
    const resultString = "0x" + resultBytes.join("");
    return resultString;
  };

  // Generates a pure random hash, useful for testing
  // i.e. not deterministic!
  function getRandomHash() {
    let result = "0x";
    for (let i = 64; i > 0; --i)
      result += "0123456789abcdef"[~~(Math.random() * 16)];
    return result;
  }

  const asyncSetTimeout = (delay) => new Promise(resolve => setTimeout(resolve, delay));

  var random = /*#__PURE__*/Object.freeze({
    __proto__: null,
    value: value,
    set_seed: set_seed,
    boolean: boolean,
    chance: chance,
    range: range,
    rangeFloor: rangeFloor,
    pick: pick,
    shuffle: shuffle,
    insideCircle: insideCircle,
    weighted: weighted,
    gaussian: gaussian,
    createHashFromHashes: createHashFromHashes,
    getRandomHash: getRandomHash,
    asyncSetTimeout: asyncSetTimeout
  });

  /**
   * A set of math utilities. Mostly taken from canvas-sketch-util.
   *
   * Reference:
   * https://github.com/mattdesl/canvas-sketch-util/blob/master/math.js
   */

  const clamp = (value, min, max) => Math.max(Math.min(value, max), min);
  const clamp01 = (v) => clamp(v, 0, 1);

  /**
   * A set of color utilities: contrast, OKLAB, hex, RGB, etc.
   *
   * Reference:
   * https://github.com/mattdesl/canvas-sketch-util/blob/master/math.js
   * https://github.com/nschloe/colorio
   */

  function lchToLab(L, C, H) {
    H = H * Math.PI / 180; // Convert Hue to radians
    let a = C * Math.cos(H);
    let b = C * Math.sin(H);
    return [L, a, b];
  }

  function labToXYZ(L, a, b) {
    let y = (L + 16) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;

    // Using D65 standard illuminant
    const Xn = 95.047;
    const Yn = 100.000;
    const Zn = 108.883;

    x = Xn * labToXYZHelper(x);
    y = Yn * labToXYZHelper(y);
    z = Zn * labToXYZHelper(z);

    return [x, y, z];
  }

  function labToXYZHelper(t) {
    const delta = 6 / 29;
    if (t > delta) {
        return Math.pow(t, 3);
    } else {
        return 3 * Math.pow(delta, 2) * (t - (4 / 29));
    }
  }

  function xyzToRgb(x, y, z) {
    // Convert to sRGB using the matrix
    x /= 100; y /= 100; z /= 100; // Normalize XYZ values

    let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    let b = x * 0.0557 + y * -0.2040 + z * 1.0570;

    // Linearize
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1/2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1/2.4) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1/2.4) - 0.055 : 12.92 * b;

    // Clamp and scale
    r = Math.min(Math.max(0, r), 1);
    g = Math.min(Math.max(0, g), 1);
    b = Math.min(Math.max(0, b), 1);

    return [r * 255, g * 255, b * 255];
  }

  function rgbToHsl(r, g, b) {
    r /= 255, g /= 255, b /= 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }

        h /= 6;
    }

    return [h * 360, s * 100, l * 100];
  }


  function Lch(L, c, h) {
    let [labL, labA, labB] = lchToLab(L, c, h);
    let [x, y, z] = labToXYZ(labL, labA, labB);
    let [r, g, b] = xyzToRgb(x, y, z);
    let [hslH, hslS, hslL] = rgbToHsl(r, g, b);
    //return `lch(${L}% ${c} ${h})`;
    return `hsl(${hslH} ${hslS}% ${hslL}%)`;
  }

  const normalizeHue = (hue) => ((hue = hue % 360) < 0 ? hue + 360 : hue);
  const normalizeLightness = (lightness) => ((lightness = lightness % 100) < 0 ? lightness + 100 : lightness);
  const normalizeChroma = (chroma) => ((chroma = chroma % 100) < 0 ? chroma + 100 : chroma);

  const metalColorValues = {
      platnum: [93.63, 1.11, 85.61],
      gold: [81.32, 83.16, 81.69],
      silver: [80.73, 4.25, 223.44],
      copper: [55.23, 51.77, 62.41]
    };
    
    const metals = [
      { chance: 0.99, name: "None", value: false },
      { chance: 0.0008, name: "Gold", value: metalColorValues.gold },
      { chance: 0.003, name: "Silver", value: metalColorValues.silver },
      { chance: 0.006, name: "Copper", value: metalColorValues.copper },
      { chance: 0.0002, name: "Platnum", value: metalColorValues.platnum },
    ];

    const filterModifiers = [
      { chance: 0.992, name: "None", value: false },
      { chance: 0.005, name: "Grayscale", value: [[0,100], 0, 0]},
      { chance: 0.002, name: "Negative", value: [[10,90], [10, 100], [0, 360]]},
      { chance: 0.001, name: "Sepia", value: [[10,90], [10, 100], [0, 360]]},
    ];

    const backgrounds = [
      { chance: 0.97, name: "None", value: false },
      { chance: 0.025, name: "Mono", value: [0, -5, 0]},
      { chance: 0.005, name: "Complementary", value: [100, -10, 180]},
    ];
    
    const selectByWeight = (listWithWeights, randomValue) => {
      var totalWeight = 0;
      var i;
    
      for (i = 0; i < listWithWeights.length; i++) {
        totalWeight += listWithWeights[i].chance;
      }
    
      var random = randomValue * totalWeight;
      for (i = 0; i < listWithWeights.length; i++) {
        if (random < listWithWeights[i].chance) {
          return listWithWeights[i];
        }
        random -= listWithWeights[i].chance;
      }
      return 0;
    };

  class Dot {

    constructor(args = {}) {
      this.random = random;
      this.hash = args.hash ? args.hash : getRandomHash();
      this.random.set_seed(this.hash);

      this.metal = selectByWeight(metals, this.random.value());
      this.compositeFilterModifier = selectByWeight(filterModifiers, this.random.value());
      this.background = selectByWeight(backgrounds, this.random.value());

      let l = (args.lightness !== undefined) ? args.lightness : this.random.range(10, 90);
      let c = (args.chroma !== undefined) ? args.chroma : this.random.range(0, 80);
      let h = (args.hue !== undefined) ? args.hue : this.random.range(0, 360);

      if (this.metal.value) {
        [l,c,h] = this.metal.value;
      }

      if (this.compositeFilterModifier.value) {
        l = this.extractRangeOrValue(this.compositeFilterModifier.value[0]);
        c = this.extractRangeOrValue(this.compositeFilterModifier.value[1]);
        h = this.extractRangeOrValue(this.compositeFilterModifier.value[2]);
      }

      this.lightness = l;
      this.chroma = c;
      this.hue = h;
      this.baseColor = [this.lightness, this.chroma, this.hue];
      this.backgroundColor = this.background.value ? [normalizeLightness(100 - (this.lightness + this.background.value[0])), normalizeChroma(100 - (this.chroma + this.background.value[1])), normalizeHue(this.hue + this.background.value[2])] : [100,0,0];
      this.lineWidth = clamp(this.random.gaussian(0.0007, 0.0002), 0.0001, 0.001);
      this.brushHeight = this.random.gaussian(400, 100);
      this.linePaths = this.generateLinePaths();
      this.zindex = (args.zindex !== undefined) ? args.zindex : this.random.rangeFloor(1, 1_000_000_000);
      this.orderingNumber = this.random.rangeFloor(1, 1_000_000_000);
    }

    extractRangeOrValue = (value) => {
      return value.constructor === Array ? range(value[0], value[1]) : value;
    }

    getAngle = () => {
      return Math.round(this.random.value() * 180 * Math.PI) / 180;
    };

    generateLinePaths() {
      const random = this.random;

      const size = clamp(random.gaussian(0.165, 0.05), 0.05, 0.35);
      const sizeC = 1 - size * 2;
      const numOfStrokes = Math.floor(
        (random.range(200, 800) / this.brushHeight) * 30
      );
      const linePaths = Array(numOfStrokes)
        .fill()
        .map(() => {
          let magnitude = clamp01(random.gaussian(0.1, 0.05));
          let variability = sizeC * 0.03;
          let linex1 = 0 + random.range(size - variability, size + variability);
          let linex2 = 1 - random.range(size - variability, size + variability);
          let liney = random.range(0.48, 0.52);
          let length = linex2 - linex1;
          let xcenter = linex1 + length / 2;
          let lineWidth = random.gaussian(this.lineWidth, this.lineWidth * 0.1);
          this.centerX = xcenter;
          this.centerY = liney;

          let ydirection = random.pick([-1, 1]);
          let cpx = random.range(linex1 + length * 0.25, linex1 + length * 0.75);
          let cpy =
            ydirection == -1
              ? liney - sizeC * magnitude
              : liney + sizeC * magnitude;
          let curveDirection = random.pick([-1, 1]);

          let strokes = [];
          let angle = this.getAngle();
          let yInc =
            random.range(lineWidth * 0.3, lineWidth * 0.5) * (1 - magnitude);
          let lightnessVariation = random.gaussian(10, 2);
          for (let i = 0; i < this.brushHeight; i++) {
            strokes.push({
              x1Padding: random.range(linex1, linex1 + sizeC * 0.02),
              x2Padding: random.range(linex2, linex2 + sizeC * 0.02),
              lineWidth,
              yInc: random.gaussian(yInc, lineWidth * 0.05),
              color: [
                clamp(
                  random.range(
                    this.baseColor[0] - lightnessVariation,
                    this.baseColor[0] + lightnessVariation
                  ),
                  0,
                  100
                ),
                this.baseColor[1],
                this.baseColor[2]
                  ],
            });
          }

          return [
            linex1,
            linex2,
            liney,
            cpx,
            cpy,
            curveDirection,
            strokes,
            angle,
          ];
        });

      return linePaths;
    }

    draw({ context, scale, dim, tx, ty, colorModifier}) {
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      this.linePaths.forEach(
        ([x1, x2, y, cpx, cpy, curveDirection, strokes, angle]) => {
          const rotationX = 0.5;
          const rotationY = 0.5;
          context.translate(tx + rotationX * scale, ty + rotationY * scale);
          context.rotate(angle);
          context.translate(-(tx + rotationX * scale), -(ty + rotationY * scale));

          strokes.forEach((stroke) => {
            const color = colorModifier ? colorModifier(stroke.color) : stroke.color;
            context.strokeStyle = Lch(...color);
            const rasterLineWidth = scale * stroke.lineWidth;
            context.lineWidth = rasterLineWidth;

            context.beginPath();
            context.moveTo(tx + stroke.x1Padding * scale, ty + y * scale);
            context.quadraticCurveTo(
              tx + cpx * scale,
              ty + cpy * scale,
              tx + stroke.x2Padding * scale,
              ty + y * scale
            );
            context.stroke();

            y = curveDirection == -1 ? y - stroke.yInc : y + stroke.yInc;
            cpy = curveDirection == -1 ? cpy - stroke.yInc : cpy + stroke.yInc;
          });

          context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
        }
      );
    }
  }

  const setContext = (context, color) => {
    context.fillStyle = color;
  };

  const drawComposite = (hashFromDots, dotHashes) => {
    set_seed(hashFromDots);

    const findColumnNum = (n) => {
      // Calculate the discriminant
      const discriminant = Math.sqrt(1 + 4 * n);

      // Use the quadratic formula to find the positive root
      const root = (-1 + discriminant) / 2;

      return root;
    };

    const compareZindex = (a, b) => {
      return a.zindex - b.zindex;
    };

    const adjustColorToFilter = (color, filter) => {
      if (!filter) {
        return color
      }

      switch (filter['name']) {
        case "Grayscale":
          return [color[0], 0, 0];
        case "Negative":
          return [100 - color[0], color[1], normalizeHue(color[2] + 180)];
        case "Sepia":
          return [color[0], 39.2,  63.25];
        default:
          return color;
      }
    };

    const modifiers = {};
    const dots = dotHashes.map((hash) => {
      let dot = new Dot({ hash });

      if (dot.background.value) {
        modifiers.background = dot.backgroundColor;
      }

      if (dot.compositeFilterModifier.value) {
        modifiers.filter = dot.compositeFilterModifier;
      }

      return dot;
    });

    const backgroundValues = modifiers.background ? modifiers.background : [100,0,0];
    const background = Lch(...adjustColorToFilter(backgroundValues, modifiers.filter));
    const numItems = dots.length;
    let columns = findColumnNum(numItems);
    let rows = Math.ceil(columns * 1.2);
    let emptySpaces = 0;
    let totalSpaces = rows * columns;
    const gridedOrdered = [];

    // fit check
    if (columns % 1 != 0) {
      columns = Math.ceil(columns);
      rows = Math.ceil(rows);
      totalSpaces = rows * columns;
      emptySpaces = totalSpaces - numItems;

      const distances = {};
      for (let i = 0; i < dots.length - 1; i++) {
        let currentDot = dots[i];
        let nextDot = dots[i + 1];
        let distance = nextDot.orderingNumber - currentDot.orderingNumber;

        distances[i] = distance;
      }

      let entries = Object.entries(distances);
      entries.sort((a, b) => a[1] - b[1]);
      gridedOrdered.push(...dots);
      for (let i = 0; i < emptySpaces; i++) {
        let entry = entries[i];
        gridedOrdered.splice(
          entry[0],
          0,
           new Dot({
            lightness: 95,
            chroma: 0,
            hue: 0,
            zindex: 0,
          })
        );
      }
    } else {
      gridedOrdered.push(...dots);
    }

    const gridedZindexed = [...gridedOrdered];
    gridedZindexed.sort(compareZindex);

    return async (context, width, height) => {
      const margin = width * 0.1;
      const dotSize = (width - margin * 2) / columns;
      const gridHeight = dotSize * rows;
      const gridMargin = (height - gridHeight) / 2;

      setContext(context, background);
      context.fillRect(0, 0, width, height);

      // draw dots
      for (let index = 0; index < gridedZindexed.length; index++) {
        const dot = gridedZindexed[index];
        const order = gridedOrdered.indexOf(dot);
        const col = order % columns;
        const row = Math.floor(order / columns);
        const dotX = margin + col * dotSize;
        const dotY = gridMargin + row * dotSize;

        const colorModifier = modifiers.filter ? (color) => { return adjustColorToFilter(color, modifiers.filter) } : null;

        dot.row = row;
        dot.col = col;
        dot.order = order;

        dot.draw({
          context,
          scale: dotSize * 1.75,
          dim: dotSize,
          tx: dotX - dotSize / 3,
          ty: dotY - dotSize / 3,
          colorModifier
        });

        await asyncSetTimeout(0); // This allows the UI to update
      }
    };
  };

  var sketch = (hashFromDots, dotHashes) => {
    return drawComposite(hashFromDots, dotHashes);
  };

    const canvas = container.appendChild(document.createElement("canvas"));
    const context = canvas.getContext("2d");
    let width, height, pixelRatio;

    const resizeCanvas = () => {
      width = container.getBoundingClientRect().width;
      height = container.getBoundingClientRect().height;
      width = width > height ? height * 0.75 : width;
      height = width > height ? height : width * 1.333;
      pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };

    const draw = () => {
      context.save();
      context.scale(pixelRatio, pixelRatio);
      // Assuming render is a function that takes (context, width, height)
      console.log(width, height)
      render(context, width, height);
      context.restore();
    };

    const dotHashes = dotsPassedIn.map((dot) => dot.hash);
    const hashFromDots = createHashFromHashes(dotHashes);
    let render = sketch(hashFromDots, dotHashes);

    // Set up canvas size and draw
    resizeCanvas();
    draw();
}